import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Alert,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { Treemap, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import {
  getGenericMarketTreemap,
  getGenericSectorTreemap,
  getGenericIndustryTreemap,
  TreemapData,
  TreemapNode,
} from '../services/api';
import { MarketType, MARKET_CONFIG } from '../pages/MarketMap';
import { formatPrice as formatCurrencyPrice, formatMarketCap as formatCurrencyMarketCap, getCurrencyDecimals } from '../utils/formatCurrency';

// Color scale for performance - returns color based on % change
const getPerformanceColor = (change: number): string => {
  if (change >= 4) return '#00703c';      // Dark green
  if (change >= 3) return '#00875a';      // Green
  if (change >= 2) return '#00a86b';      // Medium green
  if (change >= 0) return '#3b5b39';      // Muted green (0-2%)
  if (change >= -2) return '#642525';     // Muted red (-2 to 0%)
  if (change >= -3) return '#de350b';     // Medium red
  if (change >= -4) return '#bf2600';     // Red
  return '#8b0000';                        // Dark red
};

// Format large numbers
const formatMarketCap = (value: number): string => {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value.toLocaleString()}`;
};

// Sector label info for overlay rendering
interface SectorLabelInfo {
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  change: number;
}

// Global store for sector labels (reset before each render)
let sectorLabelsStore: SectorLabelInfo[] = [];

// Custom treemap content renderer
interface CustomContentProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  change?: number;
  value?: number;
  full_name?: string;
  stock_count?: number;
  depth?: number;
  index?: number;
  isHierarchical?: boolean;
}

// Standard content for flat treemap (sector or stock level)
const StandardTreemapContent: React.FC<CustomContentProps> = (props) => {
  const { x = 0, y = 0, width = 0, height = 0, name = '', change = 0, stock_count } = props;

  if (width <= 0 || height <= 0) return null;

  const showLabel = width > 40 && height > 25;
  const showDetails = width > 70 && height > 45;
  const showFullDetails = width > 100 && height > 60;

  const fontSize = Math.min(14, Math.max(9, width / 8));
  const smallFontSize = Math.min(11, Math.max(8, width / 10));

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: getPerformanceColor(change),
          stroke: '#1a1a2e',
          strokeWidth: 2,
          cursor: 'pointer',
        }}
      />
      {showLabel && (
        <text
          x={x + width / 2}
          y={y + height / 2 - (showDetails ? 8 : 0)}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#ffffff"
          stroke="none"
          fontSize={fontSize}
          fontWeight={600}
          style={{
            pointerEvents: 'none',
          }}
        >
          {name}
        </text>
      )}
      {showDetails && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 10}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#ffffff"
          stroke="none"
          fontSize={smallFontSize}
          style={{
            pointerEvents: 'none',
          }}
        >
          {change >= 0 ? '+' : ''}{change.toFixed(2)}%
        </text>
      )}
      {showFullDetails && stock_count && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 24}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#ffffff"
          stroke="none"
          fontSize={smallFontSize - 1}
          style={{
            pointerEvents: 'none',
          }}
        >
          {stock_count} stocks
        </text>
      )}
    </g>
  );
};

// Hierarchical content for industry map (sectors containing industries)
const HierarchicalTreemapContent: React.FC<CustomContentProps> = (props) => {
  const { x = 0, y = 0, width = 0, height = 0, name = '', change = 0, depth = 0 } = props;

  if (width <= 0 || height <= 0) return null;

  // Depth 1 = Sectors - store position for overlay and render border
  if (depth === 1) {
    // Store sector info for overlay rendering
    sectorLabelsStore.push({ name, x, y, width, height, change });

    return (
      <g>
        {/* Sector container border */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={{
            fill: 'transparent',
            stroke: '#6a6a8a',
            strokeWidth: 3,
          }}
        />
      </g>
    );
  }

  // Depth 2 = Industries (colored boxes inside sectors)
  if (depth === 2) {
    const showLabel = width > 35 && height > 22;
    const showDetails = width > 60 && height > 40;

    const fontSize = Math.min(12, Math.max(8, width / 9));
    const smallFontSize = Math.min(10, Math.max(7, width / 11));

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={{
            fill: getPerformanceColor(change),
            stroke: '#1a1a2e',
            strokeWidth: 1.5,
            cursor: 'pointer',
          }}
        />
        {showLabel && (
          <text
            x={x + width / 2}
            y={y + height / 2 - (showDetails ? 6 : 0)}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#ffffff"
            stroke="none"
            fontSize={fontSize}
            fontWeight={600}
            style={{
              pointerEvents: 'none',
            }}
          >
            {name}
          </text>
        )}
        {showDetails && (
          <text
            x={x + width / 2}
            y={y + height / 2 + 10}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#ffffff"
            stroke="none"
            fontSize={smallFontSize}
            style={{
              pointerEvents: 'none',
            }}
          >
            {change >= 0 ? '+' : ''}{change.toFixed(2)}%
          </text>
        )}
      </g>
    );
  }

  return null;
};

// Component to render sector labels as overlay (on top of industries but below tooltips)
const SectorLabelsOverlay: React.FC<{ labels: SectorLabelInfo[] }> = ({ labels }) => {
  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      {labels.map((sector, idx) => {
        const headerHeight = 20;
        const showLabel = sector.width > 50;
        const fontSize = Math.min(11, Math.max(8, sector.width / 14));

        if (!showLabel) return null;

        return (
          <g key={`${sector.name}-${idx}`}>
            {/* Header background */}
            <rect
              x={sector.x}
              y={sector.y}
              width={sector.width}
              height={headerHeight}
              fill="#1a1a2e"
              fillOpacity={0.9}
            />
            {/* Sector name */}
            <text
              x={sector.x + 6}
              y={sector.y + headerHeight / 2 + 1}
              textAnchor="start"
              dominantBaseline="middle"
              fill="#ffffff"
              fontSize={fontSize}
              fontWeight={700}
            >
              {sector.name}
            </text>
            {/* Sector change % */}
            {sector.width > 100 && (
              <text
                x={sector.x + sector.width - 6}
                y={sector.y + headerHeight / 2 + 1}
                textAnchor="end"
                dominantBaseline="middle"
                fill={sector.change >= 0 ? '#57d9a3' : '#ff8f73'}
                fontSize={fontSize - 1}
                fontWeight={600}
              >
                {sector.change >= 0 ? '+' : ''}{sector.change.toFixed(2)}%
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// Main custom content renderer that picks the right renderer
const CustomTreemapContent: React.FC<CustomContentProps> = (props) => {
  if (props.isHierarchical) {
    return <HierarchicalTreemapContent {...props} />;
  }
  return <StandardTreemapContent {...props} />;
};

// Custom tooltip
interface TooltipPayload {
  name: string;
  full_name?: string;
  value: number;
  change: number;
  sector?: string;
  industry?: string;
  price?: number;
  stock_count?: number;
}

const CustomTooltip: React.FC<{ active?: boolean; payload?: { payload: TooltipPayload }[]; currency?: string }> = ({
  active,
  payload,
  currency = 'USD',
}) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <Paper
      sx={{
        p: 1.5,
        maxWidth: 280,
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0,0,0,0.15)',
        zIndex: 1000,
        position: 'relative',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" color="black">
        {data.full_name || data.name}
      </Typography>
      {data.sector && (
        <Typography variant="caption" color="grey.700" display="block">
          {data.sector} {data.industry ? `> ${data.industry}` : ''}
        </Typography>
      )}
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2" color="grey.800">
          Market Cap: {formatCurrencyMarketCap(data.value, currency)}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: data.change >= 0 ? '#1b7d46' : '#c41e3a',
            fontWeight: 'bold',
          }}
        >
          {data.change >= 0 ? '+' : ''}{data.change.toFixed(2)}%
        </Typography>
        {data.price && (
          <Typography variant="body2" color="grey.800">
            Price: {formatCurrencyPrice(data.price, currency)}
          </Typography>
        )}
        {data.stock_count && (
          <Typography variant="body2" color="grey.700">
            {data.stock_count} stocks in sector
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// Legend component
const ColorLegend: React.FC = () => {
  const legendItems = [
    { label: '>4%', color: '#00703c' },
    { label: '2-4%', color: '#00a86b' },
    { label: '0-2%', color: '#3b5b39' },
    { label: '0 to -2%', color: '#642525' },
    { label: '-2 to -4%', color: '#de350b' },
    { label: '<-4%', color: '#8b0000' },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
        Performance:
      </Typography>
      {legendItems.map(({ label, color }) => (
        <Chip
          key={label}
          label={label}
          size="small"
          sx={{
            backgroundColor: color,
            color: '#fff',
            fontSize: '0.65rem',
            height: 20,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      ))}
    </Box>
  );
};

export type ViewMode = 'sector' | 'industry';

// Flattened industry data for the performance list (all timeframes)
interface IndustryRow {
  name: string;
  sector: string;
  perf: Record<string, number>; // timeframe -> change %
}

const ALL_TIMEFRAMES = ['1D', '1W', '1M', '3M', 'YTD', '1Y'] as const;
type Timeframe = typeof ALL_TIMEFRAMES[number];
type SortField = 'name' | 'sector' | Timeframe;
type SortDir = 'asc' | 'desc';

// Sector color mapping for chips
const SECTOR_COLORS: Record<string, string> = {
  'Technology': '#0ea5e9',
  'Healthcare': '#22c55e',
  'Financial Services': '#f59e0b',
  'Consumer Cyclical': '#ec4899',
  'Communication Services': '#8b5cf6',
  'Industrials': '#6366f1',
  'Consumer Defensive': '#14b8a6',
  'Energy': '#f97316',
  'Utilities': '#06b6d4',
  'Real Estate': '#a855f7',
  'Basic Materials': '#84cc16',
};

// Helper to flatten treemap data into industry rows keyed by sector+name
const flattenTreemap = (data: TreemapData): Map<string, { name: string; sector: string; change: number }> => {
  const map = new Map<string, { name: string; sector: string; change: number }>();
  for (const sector of data.children || []) {
    for (const industry of sector.children || []) {
      map.set(`${sector.name}||${industry.name}`, {
        name: industry.name,
        sector: sector.name,
        change: industry.change ?? 0,
      });
    }
  }
  return map;
};

// Performance cell renderer
const PerfCell: React.FC<{ value: number | undefined }> = ({ value }) => {
  if (value === undefined) return <Typography variant="body2" color="text.disabled">-</Typography>;
  const color = value >= 0 ? '#36b37e' : '#ff5630';
  return (
    <Typography variant="body2" fontWeight={600} sx={{ color, fontSize: '0.8rem' }}>
      {value >= 0 ? '+' : ''}{value.toFixed(2)}%
    </Typography>
  );
};

// Industry performance list component - fetches all timeframes
const IndustryPerformanceList: React.FC<{
  marketType: MarketType;
  onIndustryClick?: (sector: string, industry: string) => void;
}> = ({ marketType, onIndustryClick }) => {
  const [sortField, setSortField] = useState<SortField>('1D');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [industries, setIndustries] = useState<IndustryRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all timeframes in parallel on mount / market change
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const results = await Promise.all(
          ALL_TIMEFRAMES.map(tf => getGenericIndustryTreemap(marketType, tf))
        );

        if (cancelled) return;

        // Build a unified list of industries with all timeframe changes
        const allKeys = new Set<string>();
        const tfMaps = ALL_TIMEFRAMES.map((tf, i) => {
          const m = flattenTreemap(results[i]);
          m.forEach((_, key) => allKeys.add(key));
          return m;
        });

        const rows: IndustryRow[] = [];
        Array.from(allKeys).forEach(key => {
          // Use the first timeframe's data for name/sector
          let name = '';
          let sector = '';
          for (let ti = 0; ti < tfMaps.length; ti++) {
            const entry = tfMaps[ti].get(key);
            if (entry) {
              name = entry.name;
              sector = entry.sector;
              break;
            }
          }

          const perf: Record<string, number> = {};
          ALL_TIMEFRAMES.forEach((tf, i) => {
            const entry = tfMaps[i].get(key);
            if (entry) perf[tf] = entry.change;
          });

          rows.push({ name, sector, perf });
        });

        setIndustries(rows);
      } catch (err) {
        console.error('Error fetching industry performance:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [marketType]);

  // Sort
  const sorted = useMemo(() => {
    const copy = [...industries];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortField === 'sector') {
        cmp = a.sector.localeCompare(b.sector) || a.name.localeCompare(b.name);
      } else {
        cmp = (a.perf[sortField] ?? 0) - (b.perf[sortField] ?? 0);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [industries, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'name' || field === 'sector' ? 'asc' : 'desc');
    }
  };

  if (loading) {
    return (
      <Paper sx={{ mt: 3, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading industry performance across all timeframes...</Typography>
      </Paper>
    );
  }

  if (industries.length === 0) return null;

  return (
    <Paper sx={{ mt: 3, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Industry Performance
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {industries.length} industries — sorted by {sortField} ({sortDir === 'desc' ? 'best first' : 'worst first'})
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 180 }}>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDir : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Industry
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 130 }}>
                <TableSortLabel
                  active={sortField === 'sector'}
                  direction={sortField === 'sector' ? sortDir : 'asc'}
                  onClick={() => handleSort('sector')}
                >
                  Sector
                </TableSortLabel>
              </TableCell>
              {ALL_TIMEFRAMES.map(tf => (
                <TableCell key={tf} align="right" sx={{ minWidth: 80 }}>
                  <TableSortLabel
                    active={sortField === tf}
                    direction={sortField === tf ? sortDir : 'desc'}
                    onClick={() => handleSort(tf)}
                  >
                    {tf}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow
                key={`${row.sector}-${row.name}`}
                hover
                sx={{
                  cursor: onIndustryClick ? 'pointer' : 'default',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
                onClick={() => onIndustryClick?.(row.sector, row.name)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {row.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.sector}
                    size="small"
                    sx={{
                      backgroundColor: `${SECTOR_COLORS[row.sector] || '#6366f1'}20`,
                      color: SECTOR_COLORS[row.sector] || '#6366f1',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 22,
                    }}
                  />
                </TableCell>
                {ALL_TIMEFRAMES.map(tf => (
                  <TableCell key={tf} align="right">
                    <PerfCell value={row.perf[tf]} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// Props interface
interface MarketTreemapProps {
  initialTimeframe?: string;
  initialSector?: string;
  showHeader?: boolean;
  height?: number;
  marketType?: MarketType;
  viewMode?: ViewMode;
}

const MarketTreemap: React.FC<MarketTreemapProps> = ({
  initialTimeframe = '1D',
  initialSector,
  showHeader = true,
  height = 600,
  marketType = 'sp500',
  viewMode = 'sector',
}) => {
  const [data, setData] = useState<TreemapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState(initialTimeframe);
  const [drillPath, setDrillPath] = useState<string[]>(['Market']);
  const [currentSector, setCurrentSector] = useState<string | null>(initialSector || null);
  const [currentIndustry, setCurrentIndustry] = useState<string | null>(null);
  const [sectorLabels, setSectorLabels] = useState<SectorLabelInfo[]>([]);
  const [overlayKey, setOverlayKey] = useState(0);

  // Fetch treemap data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let result: TreemapData;

      // Use generic API for all market types
      if (currentIndustry && currentSector) {
        // Drilling into a specific industry - fetch sector data and filter to industry stocks
        const sectorResult = await getGenericSectorTreemap(marketType, currentSector, timeframe);
        const industryData = sectorResult.children?.find(c => c.name === currentIndustry);
        if (industryData && industryData.children) {
          result = {
            name: currentIndustry,
            children: industryData.children,
            value: industryData.value,
            change: industryData.change,
            metadata: {
              ...sectorResult.metadata,
              level: 'stock',
            }
          };
        } else {
          result = sectorResult;
        }
      } else if (viewMode === 'industry' && !currentSector) {
        result = await getGenericIndustryTreemap(marketType, timeframe);
      } else if (currentSector) {
        result = await getGenericSectorTreemap(marketType, currentSector, timeframe);
      } else {
        result = await getGenericMarketTreemap(marketType, timeframe, 'sector');
      }

      setData(result);
    } catch (err) {
      console.error('Error fetching treemap data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch treemap data');
    } finally {
      setLoading(false);
    }
  }, [timeframe, currentSector, currentIndustry, marketType, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset state when market type or view mode changes
  useEffect(() => {
    setCurrentSector(initialSector || null);
    setCurrentIndustry(null);
    setDrillPath(['Market']);
  }, [marketType, initialSector, viewMode]);

  const marketLabel = MARKET_CONFIG[marketType]?.name || marketType.toUpperCase();
  const viewLabel = currentIndustry
    ? 'Stocks'
    : currentSector
      ? 'Industries'
      : viewMode === 'industry'
        ? 'Industries'
        : '';

  // Handle timeframe change
  const handleTimeframeChange = (_: React.MouseEvent<HTMLElement>, newTimeframe: string | null) => {
    if (newTimeframe) {
      setTimeframe(newTimeframe);
    }
  };

  // Handle node click (drill-down into sectors/industries)
  const handleNodeClick = (node: TreemapNode) => {
    // If already viewing individual stocks (at industry level), don't drill further
    if (currentIndustry) {
      return;
    }

    // If viewing a sector's industries, clicking an industry drills into its stocks
    if (currentSector && !currentIndustry) {
      // This is an industry node - drill into stocks
      if (node.name && node.stock_count && node.stock_count > 0) {
        setCurrentIndustry(node.name);
        setDrillPath([...drillPath, node.name]);
      }
      return;
    }

    // If in industry map view (sectors containing industries), clicking an industry
    if (viewMode === 'industry' && node.sector) {
      // This is an industry node with a sector - drill into it
      setCurrentSector(node.sector);
      setCurrentIndustry(node.name);
      setDrillPath(['Market', node.sector, node.name]);
      return;
    }

    // At market level (sector view), clicking a sector drills into its industries
    if (!currentSector && node.stock_count && node.stock_count > 0) {
      setCurrentSector(node.name);
      setDrillPath([...drillPath, node.name]);
    }
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (index: number) => {
    if (index === 0) {
      // Back to market view
      setCurrentSector(null);
      setCurrentIndustry(null);
      setDrillPath(['Market']);
    } else if (index === 1) {
      // Back to sector view (showing industries)
      setCurrentSector(drillPath[1]);
      setCurrentIndustry(null);
      setDrillPath(['Market', drillPath[1]]);
    } else if (index === 2) {
      // At industry level (showing stocks) - already there
      setCurrentIndustry(drillPath[2]);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchData();
  };

  // Prepare data for Recharts Treemap
  const treemapData = data?.children || [];

  // Clear sector labels store before render
  sectorLabelsStore = [];

  // Trigger overlay re-render after treemap has rendered sector positions
  // Only for top-level industry map view (not when drilled into sector/industry)
  useEffect(() => {
    if (viewMode === 'industry' && !currentIndustry && !currentSector && data) {
      // Wait for treemap to render and populate sectorLabelsStore
      const timeoutId = setTimeout(() => {
        setSectorLabels([...sectorLabelsStore]);
        setOverlayKey(prev => prev + 1);
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      setSectorLabels([]);
    }
  }, [data, viewMode, timeframe, currentIndustry, currentSector]);

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        background: 'rgba(255, 255, 255, 0.02)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {showHeader && (
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {marketLabel} {viewLabel ? viewLabel : 'Map'}
              </Typography>
              <Breadcrumbs sx={{ mt: 0.5 }}>
                {drillPath.map((item, index) => (
                  <Link
                    key={`${item}-${index}`}
                    component="button"
                    underline="hover"
                    color={index === drillPath.length - 1 ? 'text.primary' : 'primary'}
                    onClick={() => handleBreadcrumbClick(index)}
                    sx={{ cursor: 'pointer', fontSize: '0.875rem' }}
                  >
                    {item}
                  </Link>
                ))}
              </Breadcrumbs>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Refresh data">
                <IconButton size="small" onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <ToggleButtonGroup
                value={timeframe}
                exclusive
                onChange={handleTimeframeChange}
                size="small"
              >
                {['1D', '1W', '1M', '3M', 'YTD', '1Y'].map((tf) => (
                  <ToggleButton key={tf} value={tf} sx={{ px: 1.5 }}>
                    {tf}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Legend */}
          <Box sx={{ mb: 2 }}>
            <ColorLegend />
          </Box>
        </>
      )}

      {/* Treemap Visualization */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading market data...
            </Typography>
          </Box>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : treemapData.length > 0 ? (
        <Box sx={{ height, position: 'relative' }}>
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={treemapData}
              dataKey="value"
              aspectRatio={4 / 3}
              stroke="#1a1a2e"
              fill="#8884d8"
              content={
                <CustomTreemapContent
                  isHierarchical={viewMode === 'industry' && !currentIndustry && !currentSector}
                />
              }
              isAnimationActive={false}
              onClick={(node: any) => {
                if (node && node.name) {
                  handleNodeClick(node as TreemapNode);
                }
              }}
            >
              <RechartsTooltip content={<CustomTooltip currency={MARKET_CONFIG[marketType]?.currency || 'USD'} />} />
            </Treemap>
          </ResponsiveContainer>
          {/* Sector labels overlay for hierarchical view (only at top-level industry map) */}
          {viewMode === 'industry' && !currentIndustry && !currentSector && sectorLabels.length > 0 && (
            <SectorLabelsOverlay key={overlayKey} labels={sectorLabels} />
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: height }}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      )}

      {/* Footer Stats */}
      {data && !loading && (
        <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Total Market Cap: {formatCurrencyMarketCap(data.value || 0, MARKET_CONFIG[marketType]?.currency || 'USD')}
          </Typography>
          {data.change !== undefined && (
            <Typography
              variant="body2"
              sx={{ color: data.change >= 0 ? '#36b37e' : '#ff5630' }}
              title={data.metadata?.change_source === 'index' && data.constituent_change !== undefined
                ? `The index's own move. Today's members, weighted by size at the start of the period, sum to ${data.constituent_change >= 0 ? '+' : ''}${data.constituent_change.toFixed(2)}%; the index also counts members it has since replaced and weights by tradable shares.`
                : undefined}
            >
              {data.metadata?.change_source === 'index' ? `${data.metadata.market_name || 'Index'}` : 'Overall'}: {data.change >= 0 ? '+' : ''}{data.change.toFixed(2)}%
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Timeframe: {timeframe}
          </Typography>
          {data.metadata?.generated_at && (
            <Typography variant="body2" color="text.secondary">
              Updated: {new Date(data.metadata.generated_at).toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      )}

      {/* Industry Performance List (below treemap, only for industry view at top level) */}
      {viewMode === 'industry' && !currentSector && !currentIndustry && !loading && (
        <IndustryPerformanceList
          marketType={marketType}
          onIndustryClick={(sector, industry) => {
            setCurrentSector(sector);
            setCurrentIndustry(industry);
            setDrillPath(['Market', sector, industry]);
          }}
        />
      )}
    </Paper>
  );
};

export default MarketTreemap;
